import { BillingClientError } from '@okrapdf/protocol/http-billing-client';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Data, Effect } from 'effect';
import { billingClient } from '../billingClient';
import { okraApi } from '../okraApi';
import type {
  BillingUsageResponse,
  ApiKeyRecord,
  CreateApiKeyResponse,
  WebhookSubscriptionRecord,
  CreateWebhookSubscriptionResponse,
  WebhookEvent,
  WebhookSource,
} from '../types';

/**
 * [A.6.5b] Billing endpoints delegate to the shared contract client
 * (`@okrapdf/protocol/http-billing-client`) — URLs, query encoding, and response
 * shapes are owned by the contract package; this module only maps results into
 * RTK Query's `{ data } | { error }` union and keeps the cache tags stable.
 * The route prefix is deliberately not spelled here: the contract-ownership
 * guard lets only the `billingClient.ts` seam name it.
 *
 * API keys and webhook subscriptions are NOT part of this slice. They keep the
 * `Effect.try` URL builders below until their own contract groups land.
 */

export class BillingEndpointError extends Data.TaggedError('BillingEndpointError')<{
  readonly operation: 'revoke-url' | 'webhook-url';
}> {}

const billingEndpointCauses = new WeakMap<BillingEndpointError, unknown>();

function billingEndpointFailure(
  operation: BillingEndpointError['operation'],
  cause: unknown,
): BillingEndpointError {
  const error = new BillingEndpointError({ operation });
  billingEndpointCauses.set(error, cause);
  return error;
}

export function billingEndpointErrorCause(error: BillingEndpointError): unknown {
  return billingEndpointCauses.get(error);
}

export const revokeApiKeyUrlEffect = Effect.fn('web.api.billing.revokeUrl')(function* (keyId: string) {
  return yield* Effect.try({
    try: () => `/v1/api-keys/${encodeURIComponent(keyId)}`,
    catch: (cause) => billingEndpointFailure('revoke-url', cause),
  });
});

export const webhookSubscriptionUrlEffect = Effect.fn('web.api.billing.webhookUrl')(function* (
  id: string,
) {
  return yield* Effect.try({
    try: () => `/v1/webhooks/${encodeURIComponent(id)}`,
    catch: (cause) => billingEndpointFailure('webhook-url', cause),
  });
});

function revokeApiKeyUrl(keyId: string): string {
  return Effect.runSync(revokeApiKeyUrlEffect(keyId).pipe(Effect.orDie));
}

function webhookSubscriptionUrl(id: string): string {
  return Effect.runSync(webhookSubscriptionUrlEffect(id).pipe(Effect.orDie));
}

function toQueryError(error: unknown): { error: FetchBaseQueryError } {
  if (error instanceof BillingClientError) {
    const data = { message: error.message, code: error.code, next_actions: error.nextActions };
    if (typeof error.status === 'number') return { error: { status: error.status, data } };
    return { error: { status: 'CUSTOM_ERROR', error: error.message, data } };
  }
  const message = error instanceof Error ? error.message : 'Billing request failed';
  return { error: { status: 'CUSTOM_ERROR', error: message, data: { message } } };
}

export const billingEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    getBillingUsage: build.query<BillingUsageResponse, void>({
      async queryFn() {
        try {
          return { data: await billingClient.getBillingUsage() };
        } catch (error) {
          return toQueryError(error);
        }
      },
      providesTags: ['BillingUsage'],
    }),
    createBillingCheckoutSession: build.mutation<{ url: string }, { amountCents: number }>({
      async queryFn({ amountCents }) {
        try {
          return { data: await billingClient.createCheckoutSession({ amountCents }) };
        } catch (error) {
          return toQueryError(error);
        }
      },
    }),
    confirmBillingTopupSession: build.mutation<
      {
        sessionId: string;
        paymentStatus: string | null;
        amountTotalCents: number;
        bonusCents: number;
        totalCreditedCents: number;
        newBalanceCents: number;
        applied: boolean;
      },
      { sessionId: string }
    >({
      async queryFn({ sessionId }) {
        try {
          return { data: await billingClient.getTopupSession({ sessionId }) };
        } catch (error) {
          return toQueryError(error);
        }
      },
      invalidatesTags: ['BillingUsage'],
    }),
    // [A.6.8] Verify a card (no charge) to claim the $5 signup credit.
    createBillingCardSetupSession: build.mutation<{ url: string }, void>({
      async queryFn() {
        try {
          return { data: await billingClient.createCardSetupSession() };
        } catch (error) {
          return toQueryError(error);
        }
      },
    }),
    confirmBillingCardSetupSession: build.mutation<
      {
        sessionId: string;
        status: 'granted' | 'already_claimed' | 'card_already_used' | 'incomplete';
        grantedCents: number;
        newBalanceCents: number;
      },
      { sessionId: string }
    >({
      async queryFn({ sessionId }) {
        try {
          return { data: await billingClient.getCardSetupSession({ sessionId }) };
        } catch (error) {
          return toQueryError(error);
        }
      },
      invalidatesTags: ['BillingUsage'],
    }),
    listApiKeys: build.query<{ keys: ApiKeyRecord[] }, void>({
      query: () => '/v1/api-keys',
      providesTags: ['ApiKeys'],
    }),
    createApiKey: build.mutation<CreateApiKeyResponse, { name: string }>({
      query: (body) => ({
        url: '/v1/api-keys',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    revokeApiKey: build.mutation<{ success: true }, { keyId: string }>({
      query: ({ keyId }) => ({
        url: revokeApiKeyUrl(keyId),
        method: 'DELETE',
      }),
      invalidatesTags: ['ApiKeys'],
    }),
    listWebhookSubscriptions: build.query<{ object: 'list'; data: WebhookSubscriptionRecord[] }, void>({
      query: () => '/v1/webhooks',
      providesTags: ['Webhooks'],
    }),
    createWebhookSubscription: build.mutation<
      CreateWebhookSubscriptionResponse,
      { event: WebhookEvent; target_url: string; source?: WebhookSource; facet_filter?: string | null }
    >({
      query: (body) => ({
        url: '/v1/webhooks',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Webhooks'],
    }),
    deleteWebhookSubscription: build.mutation<void, string>({
      query: (id) => ({
        url: webhookSubscriptionUrl(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Webhooks'],
    }),
  }),
});

export const {
  useGetBillingUsageQuery,
  useCreateBillingCheckoutSessionMutation,
  useConfirmBillingTopupSessionMutation,
  useCreateBillingCardSetupSessionMutation,
  useConfirmBillingCardSetupSessionMutation,
  useListApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useListWebhookSubscriptionsQuery,
  useCreateWebhookSubscriptionMutation,
  useDeleteWebhookSubscriptionMutation,
} = billingEndpoints;
